import {
  type IServiceBlock,
  useBlocksActions,
} from '@/models/commercial-proposals/blocks.store';
import { TChildService, TService } from '@/models/services';
import { BoxProps } from '@mantine/core';
import { get, set } from 'lodash-es';
import CellBase, { cellId } from '../CellBase';
import ChildServicePriceCellSettings from './ChildServiceCellSettings';
import ParentServicePriceCellSettings from './ParentServiceCellSettings';
import { IPriceCellConfig } from './ServicePriceCellSettings';
import ServicePriceDisplay from './ServicePriceDisplay';

export default function ServicePriceCell({
  record,
  service,
  block,
  tier,
  index,
  onInclusionChanged: _onInclusionChanged = () => null,
  childServices,
  ...props
}: {
  record: TService;
  service: IServiceBlock['services'][number];
  block: IServiceBlock;
  tier: number;
  index: number;
  onInclusionChanged?: (included: boolean) => void;
  childServices?: TChildService[];
} & BoxProps) {
  const { update } = useBlocksActions();
  const configKey = ['services', index, 'config', tier] as const;
  const isChildService = !!service.parentId;

  const config: IPriceCellConfig = {
    included: false,
    displayIcon: false,
    ...get(block, configKey, {}),
  };

  const Settings = isChildService
    ? ChildServicePriceCellSettings
    : ParentServicePriceCellSettings;

  const totalSumOfChildren =
    childServices?.reduce((sum, child) => sum + child.price, 0) || 0;

  const price = {
    amount: totalSumOfChildren || record.price,
    currency: record.currency,
  };

  return (
    <CellBase
      id={cellId({
        blockId: block.uuid,
        serviceId: service.serviceId,
        serviceWeight: service.weight,
        tier,
        suffix: 'price',
      })}
      settings={
        <Settings
          record={record}
          config={config}
          onChange={(nextConfig) => {
            update({
              ...set(block, configKey, nextConfig),
            });
          }}
        />
      }
      {...props}
    >
      <ServicePriceDisplay config={config} price={price} />
    </CellBase>
  );
}
